import axios from 'axios';
import { apiUrl } from '../../apiBase';

// The credential a Blender running on somebody's own machine authenticates with, and the
// add-on that uses it. See backend/blueprints/notice_addon.py for why this is not the
// session: the add-on holds no cookies and works for hours, where the access token is
// good for fifteen minutes by design.

// What the page may know about an installed key. Never the key itself — only its hash is
// stored, so after the one moment it is created there is nothing to show but its last
// four characters.
//
// `lastUsedAt` and `addonVersion` are how this page answers "is it set up?", which it
// cannot answer the obvious way: a browser reads no filesystem and cannot see Blender's
// add-ons folder. What it can be told is what reached the server, and that is the better
// fact — a key that has been used proves the whole chain (installed, enabled, key pasted,
// network reachable) where a file on disk proves one step of four.
export type AddonKey = {
  preview: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
  addonVersion: string | null;
};

// The key, and the version of the add-on the server is currently handing out — so an
// installed copy older than that one can be told to update itself.
export type AddonState = { key: AddonKey | null; version: string | null };

export async function fetchAddonState(): Promise<AddonState> {
  const { data } = await axios.get<AddonState>('/api/notice/addon/key');
  return data;
}

// A new key, replacing any existing one. The raw token comes back exactly once and is
// never retrievable again.
export async function createAddonKey(): Promise<{ key: AddonKey; token: string }> {
  const { data } = await axios.post<{ key: AddonKey; token: string }>(
    '/api/notice/addon/key',
  );
  return data;
}

// Whether the copy that last called in is older than the one being served. Compared
// field by field as numbers: "1.10.0" is newer than "1.9.0", and a string compare would
// say the opposite. An unreadable version on either side is not a reason to nag.
export function isOutdated(installed: string | null, latest: string | null): boolean {
  if (!installed || !latest) return false;
  const a = installed.split('.').map(Number);
  const b = latest.split('.').map(Number);
  if ([...a, ...b].some(Number.isNaN)) return false;
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) < (b[i] ?? 0);
  }
  return false;
}

export async function revokeAddonKey(): Promise<void> {
  await axios.delete('/api/notice/addon/key');
}

// A .blend on somebody's machine that is assembling this notice. Recorded from the
// headers the add-on sends, never uploaded and never opened from here: the page's job is
// to say WHICH file to go and open, which is the question that has no answer at all once
// a notice has been put down for three weeks and there are four of them on a desktop.
//
// `owner` is set only when the file belongs to another account, because one person's four
// files are four files and two people's four files are a thing worth knowing first.
//
// A file the add-on looked for and could not find is not in this list at all — the server
// drops it, having been told it is gone (see /blend-scan). Nothing here says "missing",
// because nothing here IS missing.
export type BlendFile = {
  filename: string;
  createdAt: string | null;
  lastSeenAt: string | null;
  owner: string | null;
};

export async function fetchBlendFiles(docId: string): Promise<BlendFile[]> {
  const { data } = await axios.get<{ files: BlendFile[] }>(
    '/api/notice/addon/blend-files', { params: { docId } },
  );
  return data.files || [];
}

export const addonDownloadUrl = apiUrl('/api/notice/addon/download');

// What actually gets pasted into Blender: the server and the token in one string, split
// on the last "|" by the add-on. One field and one paste rather than two of each — and
// the server half is taken from the browser's own origin, so a key generated against a
// dev server configures the add-on for the dev server without anybody choosing.
export const connectionKey = (token: string) => `${window.location.origin}|${token}`;
